import {booleanAttribute, ChangeDetectionStrategy, Component, computed, ElementRef, Inject, Injectable, InjectionToken, input, signal, Signal, viewChild} from "@angular/core";
import {ArkivRessursClient, DokumentId, DokumentVersjonModell, DokumentVersjonOgSakerModell, SigneringStatus, SkjermingDetaljerModell, TynnSakModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Observable, of, switchMap, tap} from "rxjs";
import {dokumentSkalIkkeSigneresAvBruker, erArkivdokument, erDokumentSkjermet, skjermetEntitetTypeForDokument} from "felles-ng2/modell/dokumentModellHjelpere";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {TranslateModule} from "@ngx-translate/core";
import {FormaterFilstørrelsePipe} from "felles-ng2/pipes/FormaterFilstørrelsePipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {DokumentKortvisningKomponent} from "../dokument-kortvisning/dokument-kortvisning";
import {DokumentFormatIkonKomponent} from "../dokument-format-ikon/dokument-format-ikon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {TittelVerdiSamlingKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi-samling";
import {TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {DokumentTilSigneringStatusKomponent} from "web-ng2/moduler/dokument/komponenter/dokument-til-signering-status/dokument-til-signering-status";
import {MatTooltip} from "@angular/material/tooltip";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {MatMenuTrigger} from "@angular/material/menu";
import {DokumentMenyKomponent} from "web-ng2/moduler/dokument/komponenter/dokument-meny/dokument-meny";
import {åpneINyttvindu} from "felles-ng2/støttefunksjoner/nettleser";
import {SkjermingVisKomponent} from "web-ng2/moduler/skjerming/komponenter/skjerming-vis/skjerming-vis";
import {SkjermetEntitetType, SkjermingDatatjeneste} from "felles-ng2/tjenester/datalager/SkjermingDatatjeneste";
import {mapTilData, terminerEtterFørsteRequestRespons} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {tomSkjermingDetaljerModell} from "felles-ng2/modell/tommeModeller";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {DokumentversjonListeKomponent} from "web-ng2/moduler/dokument/komponenter/dokumentversjon-liste/dokumentversjon-liste";
import {DokumentFormatInfo, DokumentInformasjonTjeneste} from "felles-ng2/tjenester/DokumentInformasjonTjeneste";
import {ÅpneDokumentTjeneste} from "web-ng2/moduler/dokument/tjenester/ÅpneDokumentTjeneste";
import {DokumentSigneringTjeneste} from "web-ng2/moduler/dokument/tjenester/DokumentSigneringTjeneste";
import {SkjermingDialogKomponent} from "web-ng2/moduler/skjerming/modale/skjerming-dialog/skjerming-dialog";
import {MatDialog} from "@angular/material/dialog";
import {distinctEntitetIdUntilChanged, distinctUntilChangedDeep, logHendelser} from "felles-ng2/støttefunksjoner/observable";

const debug = initDebugger('DokumentDetaljerKomponent');

type Forsidetype = 'infisert' | 'virussjekk' | 'forside' | 'ikon';

/**  InjectionToken for injisering av {@link DelegerteDokumentDetaljerOperasjoner} implementasjon. */
export const DelegerteDokumentDetaljerOperasjoner_DI_TOKEN = new InjectionToken<DelegerteDokumentDetaljerOperasjoner>('DelegerteDokumentDetaljerOperasjoner implementasjon');

/**
 * Viser detaljer for valgt dokument
 */
@Component({
    selector: 'dokument-detaljer',
    templateUrl: 'dokument-detaljer.html',
    styleUrl: 'dokument-detaljer.scss',
    host: {'data-testid': 'dokument-detaljer'},
    imports: [
        TranslateModule,
        FormaterFilstørrelsePipe,
        FormaterPlainDatePipe,
        ZonedDateTimeSomPlainDatePipe,
        DokumentKortvisningKomponent,
        DokumentFormatIkonKomponent,
        MatButton,
        MatIcon,
        TittelVerdiSamlingKomponent,
        TittelVerdiKomponent,
        VerdiKomponent,
        DokumentTilSigneringStatusKomponent,
        MatTooltip,
        TipsKomponent,
        FormaterZonedDateTimePipe,
        MatIconButton,
        MatMenuTrigger,
        DokumentMenyKomponent,
        SkjermingVisKomponent,
        DokumentversjonListeKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DokumentDetaljerKomponent {

    private versjonerBlokk = viewChild<TittelVerdiKomponent>('versjonerBlokk');
    private signeringBlokk = viewChild<TittelVerdiKomponent>('signeringBlokk');
    private sakerArkivdokumentBehandlesIBlokk = viewChild<TittelVerdiKomponent>('sakerArkivdokumentBehandlesIBlokk');
    private skjermingBlokk = viewChild<TittelVerdiKomponent>('skjermingBlokk');

    readonly dokumentversjon = input.required<DokumentVersjonModell>();
    readonly redigerbar = input(false, {transform: booleanAttribute});

    readonly erArkivdokument = computed(() => erArkivdokument(this.dokumentversjon()));
    readonly informasjon = computed(() => this.dokumentInformasjon.dokumentinformasjon(this.dokumentversjon()));
    readonly skjerming: Signal<SkjermingDetaljerModell>;
    readonly skjermetEntitetType = computed(() => skjermetEntitetTypeForDokument(this.dokumentversjon()));
    readonly harMultipleVersjoner = computed(() => this.dokumentversjon().versjonsnummer > 1);
    /** Saker arkivdokument behandles i, kun aktuelt dersom dokumentet er et arkivdokument */
    readonly sakerArkivdokumentetBehandlesI: Signal<DokumentVersjonOgSakerModell[]>;
    readonly sendTilSigneringMulig = computed(() => this.dokumentSigneringTjeneste.sendTilSigneringMulig(this.dokumentversjon()) && this.redigerbar());

    readonly signeringsstatuser = SigneringStatus;

    constructor(private avhengigheter: DokumentDetaljerKomponentAvhengigheter,
                private el: ElementRef<HTMLElement>,
                private dokumentInformasjon: DokumentInformasjonTjeneste,
                protected dokumentSigneringTjeneste: DokumentSigneringTjeneste,
                private navigasjon: Navigasjon,
                private matDialog: MatDialog,
                @Inject(DelegerteDokumentDetaljerOperasjoner_DI_TOKEN) private delegerteOperasjoner: DelegerteDokumentDetaljerOperasjoner) {
        //distinctUntilChangedDeep for å unngå unødvendige kall til sakerArkivdokumentBehandlesI/skjermingsdetaljer
        //merk at kall til skjermingsdetaljer vil fører til at ny verdi blir levert til dokumentversjon,
        //distinctUntilChangedDeep forhindrer at dette fører til nytt unødvendig kall til sakerArkivdokumentBehandlesI/skjermingsdetaljer
        const dokumentversjonSomObserverbar = toObservable(this.dokumentversjon).pipe(distinctUntilChangedDeep());

        this.sakerArkivdokumentetBehandlesI = toSignal(dokumentversjonSomObserverbar
          .pipe(
            //'behandles i sak' er kun aktuelt for arkivdokumenter
            switchMap(dokumentversjon => erArkivdokument(dokumentversjon) ?
              this.avhengigheter.sakerArkivdokumentBehandlesI(dokumentversjon.dokumentId) :
              of([]))
          ), {initialValue: []});

        this.skjerming = toSignal(dokumentversjonSomObserverbar
          .pipe(
            //kun laste skjermingsinfo dersom dokumentet er skjermet
            switchMap(dokumentversjon => erDokumentSkjermet(dokumentversjon) ?
              this.avhengigheter.skjermingsdetaljer(dokumentversjon.dokumentId, skjermetEntitetTypeForDokument(dokumentversjon)!) :
              of(tomSkjermingDetaljerModell())),
          ), {initialValue: tomSkjermingDetaljerModell()});

        //sørger for at fokusert blokk forsvinner når man endrer hvilket dokument man ser på
        dokumentversjonSomObserverbar.subscribe(() => this.resetFokusertBlokk());
    }

    /** scroller til toppen av dokument-detaljer blokken */
    fokuser() {
        this.el.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    /** scroller til versjons-blokken og gir denne visuelt fokus */
    fokuserVersjonerBlokk() {
        this.versjonerBlokk()?.fokuser();
    }

    /** scroller til signering-blokken og gir denne visuelt fokus */
    fokuserSigneringBlokk() {
        this.signeringBlokk()?.fokuser();
    }

    /** scroller til saker-arkivdokument-behandles-i-blokken og gir denne visuelt fokus */
    fokuserSakerArkivdokumentBehandlesIBlokk() {
        this.sakerArkivdokumentBehandlesIBlokk()?.fokuser();
    }

    /** scroller til skjerming-blokken og gir denne visuelt fokus */
    fokuserSkjermingBlokk() {
        this.skjermingBlokk()?.fokuser();
    }

    sendTilSignering() {
        this.dokumentSigneringTjeneste.sendTilSignering(this.dokumentversjon());
    }

    signaturinformasjon() {
        //for å unngå unødvendig mye datalasting returnerer vi her 'bruker skal ikke signere'
        //antar at dette er ok da knapp signering uansett vises i 'signering'-blokken
        return dokumentSkalIkkeSigneresAvBruker();
    }

    redigerSkjerming() {
        SkjermingDialogKomponent.redigerForDokument(this.matDialog, this.dokumentversjon());
    }

    aapneInline() {
        this.delegerteOperasjoner.åpneDokumentInline(this.dokumentversjon());
    }

    aapneFormat(format: DokumentFormatInfo) {
        åpneINyttvindu(format.url);
    }

    gaaTilBehandling(sak: TynnSakModell) {
        this.navigasjon.gåTilMøteOgSak(sak.moete.id, sak.id);
    }

    registrerNyVersjon() {
        this.delegerteOperasjoner.registrerNyDokumentversjon(this.dokumentversjon());
    }

    //private hjelpemetoder

    private resetFokusertBlokk() {
        this.versjonerBlokk()?.fokuser(false);
        this.signeringBlokk()?.fokuser(false);
        this.skjermingBlokk()?.fokuser(false);
        this.sakerArkivdokumentBehandlesIBlokk()?.fokuser(false);
    }
}


/** Operasjoner som dokument-detaljer-komponenten ikke selv kan realisere, men som noen høyere opp i komponent-treet må realisere */
export interface DelegerteDokumentDetaljerOperasjoner {
    /** Presenterer bruker med en fil-velger-dialog og registrerer valgt fil som ny versjon av angitt dokument, gitt at {@link DokumentDetaljerKomponent#redigerbar} ikke er satt til false. */
    registrerNyDokumentversjon(dokumentversjon: DokumentVersjonModell): void;

    /** Åpner inline visning av angitt dokument.
     * @return false dersom angitt dokument ikke finnes */
    åpneDokumentInline(dokumentversjon: DokumentVersjonModell): boolean;
}

@Injectable()
export class DokumentDetaljerKomponentAvhengigheter {

    constructor(private arkivRessurs: ArkivRessursClient,
                private skjermingDatatjeneste: SkjermingDatatjeneste) {
    }

    sakerArkivdokumentBehandlesI(dokumentId: DokumentId): Observable<DokumentVersjonOgSakerModell[]> {
        return this.arkivRessurs.sakerKnyttetTilArkivdokument(dokumentId).somObservable()
    }

    skjermingsdetaljer(id: DokumentId, type: SkjermetEntitetType): Observable<SkjermingDetaljerModell> {
        return this.skjermingDatatjeneste.hentMedMeta(id, type)
          .pipe(terminerEtterFørsteRequestRespons(), mapTilData());
    }

}
